/**
 * Maps a real `DraftRecord` (as returned by the API) into the flat shape
 * the approval-queue list rows + detail pane render. The queue design
 * (queue-web.jsx) treats a draft as a single hook/body/media/status unit;
 * a real `DraftRecord` is per-(provider, platform) and carries several
 * variants, so we pick the primary content variant and collect hashtags
 * across the rest — the same rule the generate studio uses.
 */

import type { DraftRecord } from "@notamen/core/db";
import type { MediaSpec } from "@/components/ui/MediaPreview/MediaPreview";
import { toPlatformKey, type PlatformKey } from "@/components/ui/PlatformBadge/PlatformBadge";
import type { DraftState } from "@/components/ui/StatusPill/StatusPill";

const KIND_LABEL: Record<string, string> = {
  caption: "Caption",
  carousel: "Carousel",
  script: "Video script",
  image_brief: "Image brief",
  hashtags: "Hashtags",
  hook_options: "Hook options",
};

export interface DraftView {
  /** The DRAFT# id (used for the action endpoints). */
  id: string;
  record: DraftRecord;
  platform: PlatformKey;
  /** Full platform name, for the API path-agnostic bits + labels. */
  platformName: string;
  status: DraftState;
  kindLabel: string;
  hook: string | undefined;
  body: string;
  hashtags: string[];
  media: MediaSpec | undefined;
  warnings: Array<{ level: "warn" | "block"; text: string }>;
  hasBlock: boolean;
  provider: string;
  createdAt: string;
  scheduledFor: string | undefined;
  scheduledBy: string | undefined;
  scheduledAt: string | undefined;
  approvedBy: string | undefined;
  approvedAt: string | undefined;
  rejectedReason: string | undefined;
  overrideReason: string | undefined;
}

export function toDraftView(record: DraftRecord): DraftView {
  const variants = record.draft.variants;
  const primary =
    variants.find((v) => v.kind !== "hashtags" && v.kind !== "hook_options") ?? variants[0];
  const hashtags = variants.flatMap((v) => v.hashtags ?? []);
  const media: MediaSpec | undefined =
    primary?.media ?? variants.find((v) => v.media)?.media;

  const warnings = record.draft.warnings.map((w) => ({
    level: (w.level === "block" ? "block" : "warn") as "warn" | "block",
    text: `${w.rule}: ${w.detail}`,
  }));

  return {
    id: record.draft.id,
    record,
    platform: toPlatformKey(record.platform),
    platformName: record.platform,
    status: record.status as DraftState,
    kindLabel: primary ? (KIND_LABEL[primary.kind] ?? primary.kind) : "Draft",
    hook: primary?.hook,
    body: primary?.body ?? "",
    hashtags,
    media,
    warnings,
    hasBlock: record.draft.warnings.some((w) => w.level === "block"),
    provider: record.provider,
    createdAt: record.draft.createdAt,
    scheduledFor: record.scheduledFor,
    scheduledBy: record.scheduledBy,
    scheduledAt: record.scheduledAt,
    approvedBy: record.approvedBy,
    approvedAt: record.approvedAt,
    rejectedReason: record.rejectedReason,
    overrideReason: record.overrideReason,
  };
}

/** Short, human date for list rows + detail. Falls back to the raw ISO. */
export function fmtDateTime(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "just now" / "12m ago" / "3h ago" / falls back to the short date. */
export function fmtRelative(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return fmtDateTime(iso);
}

/**
 * Display name + initials derived from an email — the v1 identity, since we
 * don't capture a separate display name anywhere yet. "ada.lovelace@x.com"
 * → { name: "ada.lovelace", initials: "AL" }.
 */
export function identityFromEmail(email: string | undefined): { name: string; initials: string } {
  const safe = (email ?? "").trim();
  if (!safe) return { name: "Unknown", initials: "?" };
  const local = safe.split("@")[0] ?? safe;
  const parts = local.split(/[.\-_+]/).filter(Boolean);
  const initials =
    parts.length >= 2
      ? `${parts[0]![0]!}${parts[1]![0]!}`.toUpperCase()
      : local.slice(0, 2).toUpperCase();
  return { name: local, initials };
}
